import { useEffect, useState } from 'react';
import Layout from '@/react-app/components/Layout';
import { 
  CalendarDays, 
  DollarSign, 
  Users, 
  Home,
  FileText,
  Download,
  Filter,
  BarChart3,
  PieChart,
  Calendar,
  ArrowUpDown
} from 'lucide-react';

interface MonthlyStats {
  totalRevenue: number;
  accommodationRevenue: number;
  externalSalesRevenue: number;
  totalReservations: number;
  totalCheckIns: number;
  totalCheckOuts: number;
  averageOccupancy: number;
  averageDailyRate: number;
  totalPayments: number;
  pendingPayments: number;
  cancelledReservations: number;
}

interface MonthlyReservation {
  id: number;
  guest_name: string;
  room_number: string;
  check_in_date: string;
  check_out_date: string;
  total_amount: number;
  payment_status: string;
  status: string;
}

interface PaymentSummary {
  method: string;
  amount: number;
  count: number;
}

interface DailyStats {
  date: string;
  revenue: number;
  checkIns: number;
  checkOuts: number;
  occupancy: number;
}

export default function Reports() {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats>({
    totalRevenue: 0,
    accommodationRevenue: 0,
    externalSalesRevenue: 0,
    totalReservations: 0,
    totalCheckIns: 0,
    totalCheckOuts: 0,
    averageOccupancy: 0,
    averageDailyRate: 0,
    totalPayments: 0,
    pendingPayments: 0,
    cancelledReservations: 0
  });
  
  const [reservations, setReservations] = useState<MonthlyReservation[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'guest' | 'amount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchMonthlyReport();
  }, [selectedMonth]);

  const fetchMonthlyReport = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const startDate = `${year}-${month}-01`;
      // Get last day of the month (month is 1-indexed in the string, need 0-indexed for Date)
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

      const [reservationsResponse, paymentsResponse, roomsResponse, externalSalesResponse] = await Promise.all([
        fetch('/api/reservations'),
        fetch(`/api/payments?start_date=${startDate}&end_date=${endDate}`),
        fetch('/api/rooms'),
        fetch(`/api/external-sales?start_date=${startDate}&end_date=${endDate}`)
      ]);

      const allReservations = await reservationsResponse.json();
      const payments = await paymentsResponse.json();
      const rooms = await roomsResponse.json();
      const externalSales = await externalSalesResponse.json();

      // Filter reservations for the selected month (EXCLUDE CANCELLED)
      const monthlyReservations = allReservations.filter((reservation: any) => {
        const checkIn = new Date(reservation.check_in_date);
        const checkOut = new Date(reservation.check_out_date);
        const monthStart = new Date(startDate);
        const monthEnd = new Date(endDate);
        
        const isInPeriod = (checkIn >= monthStart && checkIn <= monthEnd) || 
               (checkOut >= monthStart && checkOut <= monthEnd) ||
               (checkIn <= monthStart && checkOut >= monthEnd);
        
        // EXCLUDE cancelled reservations from all financial calculations
        return isInPeriod && reservation.status !== 'cancelled';
      });
      
      // Count cancelled reservations separately
      const cancelledCount = allReservations.filter((reservation: any) => {
        const checkIn = new Date(reservation.check_in_date);
        const checkOut = new Date(reservation.check_out_date);
        const monthStart = new Date(startDate);
        const monthEnd = new Date(endDate);
        
        const isInPeriod = (checkIn >= monthStart && checkIn <= monthEnd) || 
               (checkOut >= monthStart && checkOut <= monthEnd) ||
               (checkIn <= monthStart && checkOut >= monthEnd);
        
        return isInPeriod && reservation.status === 'cancelled';
      }).length;

      // Calculate stats
      const accommodationRevenue = payments.reduce((sum: number, payment: any) => sum + Number(payment.amount), 0);
      const externalSalesRevenue = externalSales.reduce((sum: number, sale: any) => sum + Number(sale.total_amount), 0);
      const totalRevenue = accommodationRevenue + externalSalesRevenue;
      const totalReservations = monthlyReservations.length;
      const totalCheckIns = monthlyReservations.filter((r: any) => r.status === 'checked_in' || r.status === 'checked_out').length;
      const totalCheckOuts = monthlyReservations.filter((r: any) => r.status === 'checked_out').length;
      
      const pendingPayments = monthlyReservations
        .filter((r: any) => r.payment_status === 'pending' || r.payment_status === 'partial')
        .reduce((sum: number, r: any) => sum + Number(r.total_amount), 0);

      const averageDailyRate = monthlyReservations.length > 0 
        ? monthlyReservations.reduce((sum: number, r: any) => sum + Number(r.daily_rate), 0) / monthlyReservations.length
        : 0;

      // Calculate occupancy (simplified)
      const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
      const totalRoomNights = rooms.length * daysInMonth;
      const occupiedNights = monthlyReservations.reduce((sum: number, reservation: any) => {
        const checkIn = new Date(Math.max(new Date(reservation.check_in_date).getTime(), new Date(startDate).getTime()));
        const checkOut = new Date(Math.min(new Date(reservation.check_out_date).getTime(), new Date(endDate).getTime()));
        const nights = Math.max(0, Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24)));
        return sum + nights;
      }, 0);
      
      const averageOccupancy = totalRoomNights > 0 ? (occupiedNights / totalRoomNights) * 100 : 0;

      // Process payment summary
      const paymentMethodStats = payments.reduce((acc: any, payment: any) => {
        const method = payment.payment_method;
        if (!acc[method]) {
          acc[method] = { amount: 0, count: 0 };
        }
        acc[method].amount += Number(payment.amount);
        acc[method].count += 1;
        return acc;
      }, {});

      const processedPaymentSummary = Object.entries(paymentMethodStats).map(([method, data]: [string, any]) => ({
        method: getPaymentMethodLabel(method),
        amount: data.amount,
        count: data.count
      }));

      // Generate daily stats
      const daily: DailyStats[] = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const currentDate = `${year}-${month}-${String(day).padStart(2, '0')}`;
        const dayReservations = monthlyReservations.filter((r: any) => {
          const checkIn = new Date(r.check_in_date);
          const checkOut = new Date(r.check_out_date);
          const current = new Date(currentDate);
          return checkIn <= current && checkOut > current;
        });

        const dayPayments = payments.filter((p: any) => 
          p.payment_date.startsWith(currentDate)
        );

        const dayRevenue = dayPayments.reduce((sum: number, p: any) => sum + Number(p.amount), 0);
        const dayCheckIns = monthlyReservations.filter((r: any) => r.check_in_date === currentDate).length;
        const dayCheckOuts = monthlyReservations.filter((r: any) => r.check_out_date === currentDate).length;
        const dayOccupancy = rooms.length > 0 ? (dayReservations.length / rooms.length) * 100 : 0;

        daily.push({
          date: currentDate,
          revenue: dayRevenue,
          checkIns: dayCheckIns,
          checkOuts: dayCheckOuts,
          occupancy: dayOccupancy
        });
      }

      setMonthlyStats({
        totalRevenue,
        accommodationRevenue,
        externalSalesRevenue,
        totalReservations,
        totalCheckIns,
        totalCheckOuts,
        averageOccupancy,
        averageDailyRate,
        totalPayments: accommodationRevenue,
        pendingPayments,
        cancelledReservations: cancelledCount
      });

      setReservations(monthlyReservations);
      setPaymentSummary(processedPaymentSummary);
      setDailyStats(daily);

    } catch (error) {
      console.error('Error fetching monthly report:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: { [key: string]: string } = {
      'cash': 'Dinheiro',
      'credit_card': 'Cartão de Crédito',
      'debit_card': 'Cartão de Débito',
      'pix': 'PIX',
      'bank_transfer': 'Transferência'
    };
    return labels[method] || method;
  };

  const sortReservations = (reservations: MonthlyReservation[]) => {
    return [...reservations].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.check_in_date).getTime() - new Date(b.check_in_date).getTime();
          break;
        case 'guest':
          comparison = a.guest_name.localeCompare(b.guest_name);
          break;
        case 'amount':
          comparison = Number(a.total_amount) - Number(b.total_amount);
          break;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  };

  const generateReport = () => {
    const [year, month] = selectedMonth.split('-');
    const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
    
    const reportContent = `
      ═══════════════════════════════════════════════════════════
                    RELATÓRIO FINANCEIRO MENSAL
                        ${monthName.toUpperCase()}
      ═══════════════════════════════════════════════════════════
      
      📊 RESUMO EXECUTIVO:
      ═══════════════════════════════════════════════════════════
      💰 Receita Total do Período: R$ ${monthlyStats.totalRevenue.toFixed(2)}
         ├─ Hospedagens: R$ ${monthlyStats.accommodationRevenue.toFixed(2)}
         └─ Vendas Externas: R$ ${monthlyStats.externalSalesRevenue.toFixed(2)}
      
      💳 Pagamentos Pendentes: R$ ${monthlyStats.pendingPayments.toFixed(2)}
      💵 Diária Média: R$ ${monthlyStats.averageDailyRate.toFixed(2)}
      
      📈 INDICADORES DE OCUPAÇÃO:
      ═══════════════════════════════════════════════════════════
      📊 Taxa de Ocupação Média: ${monthlyStats.averageOccupancy.toFixed(1)}%
      📋 Total de Reservas (Ativas): ${monthlyStats.totalReservations}
      ❌ Reservas Canceladas: ${monthlyStats.cancelledReservations}
      ✅ Check-ins Realizados: ${monthlyStats.totalCheckIns}
      🚪 Check-outs Realizados: ${monthlyStats.totalCheckOuts}
      
      💳 FORMAS DE PAGAMENTO:
      ═══════════════════════════════════════════════════════════
      ${paymentSummary.map(p => `💵 ${p.method}: R$ ${p.amount.toFixed(2)} (${p.count} pagamentos)`).join('\n      ')}
      
      🏨 RESERVAS DO PERÍODO (Excluindo Canceladas):
      ═══════════════════════════════════════════════════════════
      ${sortReservations(reservations).map(r => 
        `📌 ${r.guest_name} | Quarto ${r.room_number} | ${new Date(r.check_in_date).toLocaleDateString('pt-BR')} - ${new Date(r.check_out_date).toLocaleDateString('pt-BR')} | R$ ${Number(r.total_amount).toFixed(2)} | Status: ${r.payment_status}`
      ).join('\n      ')}
      
      ═══════════════════════════════════════════════════════════
      📅 Relatório gerado em: ${new Date().toLocaleString('pt-BR')}
      🏨 Sistema MeuHotel - Gestão Hoteleira Profissional
      ═══════════════════════════════════════════════════════════
      
      OBSERVAÇÕES IMPORTANTES:
      • Reservas canceladas NÃO são contabilizadas na receita
      • Vendas externas incluem produtos vendidos fora das hospedagens
      • Todos os valores são referentes ao período selecionado
    `;

    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-mensal-${selectedMonth}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const StatCard = ({ title, value, icon: Icon, color, format = 'number' }: {
    title: string;
    value: number;
    icon: any;
    color: string;
    format?: 'number' | 'currency' | 'percentage';
  }) => {
    const formatValue = (val: number) => {
      switch (format) {
        case 'currency':
          return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
        case 'percentage':
          return `${val.toFixed(1)}%`;
        default:
          return val.toLocaleString('pt-BR');
      }
    };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
              {loading ? '-' : formatValue(value)}
            </p>
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    );
  };

  const maxRevenue = Math.max(...dailyStats.map(d => d.revenue));

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relatórios</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Relatório mensal detalhado do hotel
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-400" />
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={generateReport}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Exportar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Main Revenue Card */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-8 mb-8">
          <div className="text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-green-100 text-sm font-medium uppercase tracking-wide">Receita Total do Período</p>
                <h2 className="text-4xl font-bold mt-2">
                  {loading ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(monthlyStats.totalRevenue)}
                </h2>
              </div>
              <DollarSign className="w-16 h-16 opacity-50" />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-green-400/30">
              <div>
                <p className="text-green-100 text-xs uppercase mb-1">Hospedagens</p>
                <p className="text-2xl font-bold">
                  {loading ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(monthlyStats.accommodationRevenue)}
                </p>
              </div>
              <div>
                <p className="text-green-100 text-xs uppercase mb-1">Vendas Externas</p>
                <p className="text-2xl font-bold">
                  {loading ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(monthlyStats.externalSalesRevenue)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <StatCard
            title="Taxa de Ocupação"
            value={monthlyStats.averageOccupancy}
            icon={Users}
            color="bg-blue-500"
            format="percentage"
          />
          <StatCard
            title="Total de Reservas"
            value={monthlyStats.totalReservations}
            icon={CalendarDays}
            color="bg-purple-500"
          />
          <StatCard
            title="Diária Média"
            value={monthlyStats.averageDailyRate}
            icon={Home}
            color="bg-orange-500"
            format="currency"
          />
          <StatCard
            title="Pagamentos Pendentes"
            value={monthlyStats.pendingPayments}
            icon={DollarSign}
            color="bg-yellow-500"
            format="currency"
          />
          <StatCard
            title="Reservas Canceladas"
            value={monthlyStats.cancelledReservations}
            icon={CalendarDays}
            color="bg-red-500"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Revenue Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Receita Diária</h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {dailyStats.map((day) => (
                <div key={day.date} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600 dark:text-gray-400 w-12">
                      {new Date(day.date).getDate()}
                    </span>
                    <div className="flex-1">
                      <div
                        className="h-4 bg-green-200 dark:bg-green-900 rounded-full overflow-hidden"
                        style={{ width: '200px' }}
                      >
                        <div
                          className="h-full bg-green-500 transition-all duration-300"
                          style={{ 
                            width: `${maxRevenue > 0 ? (day.revenue / maxRevenue) * 100 : 0}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white w-20 text-right">
                    R$ {day.revenue.toFixed(0)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Formas de Pagamento</h3>
              <PieChart className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {paymentSummary.map((method, index) => (
                <div key={method.method} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{method.method}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      R$ {method.amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {method.count} pagamentos
                    </p>
                  </div>
                </div>
              ))}
              {paymentSummary.length === 0 && !loading && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Nenhum pagamento no período
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Reservations Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reservas do Período</h3>
              <div className="mt-4 sm:mt-0 flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-1 text-sm"
                  >
                    <option value="date">Data</option>
                    <option value="guest">Hóspede</option>
                    <option value="amount">Valor</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Hóspede
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Quarto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Período
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortReservations(reservations).map((reservation) => (
                  <tr key={reservation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {reservation.guest_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {reservation.room_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(reservation.check_in_date).toLocaleDateString('pt-BR')} - {new Date(reservation.check_out_date).toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        R$ {Number(reservation.total_amount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        reservation.payment_status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                        reservation.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                        'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {reservation.payment_status === 'paid' ? 'Pago' :
                         reservation.payment_status === 'partial' ? 'Parcial' : 'Pendente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {reservations.length === 0 && !loading && (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Nenhuma reserva encontrada no período selecionado</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
